'use client';

import { useEffect, useState, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import Navbar from '@/components/Navbar';
import { useAuth } from '@/contexts/AuthContext';
import {
  Gift, Copy, CheckCircle, Users, TrendingUp, DollarSign,
  Clock, ArrowLeft, ExternalLink, UserPlus, Radio, AlertCircle, Loader2,
  Zap, Award, Repeat
} from 'lucide-react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import { parseServerDate } from '@/lib/dates';
import { useLang } from '@/lib/i18n/LanguageContext';
import { track } from '@/lib/analytics';


interface ReferralStats {
  totalReferred: number;
  convertedSubscribers: number;
  referredCreators: number;
  freeSignups: number;
  pendingRewards: number;
  approvedRewards: number;
  lifetimeEarnings: number;
  xpEarned: number;
}

interface Milestone {
  totalQualified: number;
  next: { count: number; label: string } | null;
}

interface FoundingMember {
  number: number | null;
  cap: number;
  awarded: number;
}

interface ReferralProfile {
  userId: string;
  referralCode: string;
  createdAt: string;
}

interface Attribution {
  id: string;
  referrerUserId: string;
  referredUserId: string;
  channelId: string | null;
  attributionType: string;
  status: string;
  createdAt: string;
}

interface Reward {
  id: string;
  referrerUserId: string;
  sourceUserId: string;
  channelId: string | null;
  rewardType: string;
  rewardAmount: number;
  rewardStatus: string;
  createdAt: string;
  holdbackUntil?: string | null;
  billingCycleNumber?: number | null;
  subscriptionAmount?: number | null;
}

interface ReferralData {
  profile: ReferralProfile;
  referralLink: string;
  shortLink: string;
  commissionPercent: number;
  maxBillingCycles: number;
  milestone: Milestone;
  foundingMember: FoundingMember;
  stats: ReferralStats;
  attributions: Attribution[];
  rewards: Reward[];
}

// Status pills map to the same semantic tokens used across the app: gold = pending/awaiting,
// green = approved/converted/qualified, blue = paid, red = canceled.
const STATUS_COLORS: Record<string, { bg: string; text: string; label: string }> = {
  pending:   { bg: 'var(--win-muted)',     text: 'var(--win)',     label: 'Pending'   },
  approved:  { bg: 'var(--bullish-muted)', text: 'var(--bullish)', label: 'Approved'  },
  paid:      { bg: 'var(--info-muted)',    text: 'var(--info)',    label: 'Paid'      },
  canceled:  { bg: 'var(--bearish-muted)', text: 'var(--bearish)', label: 'Canceled'  },
  converted: { bg: 'var(--bullish-muted)', text: 'var(--bullish)', label: 'Converted' },
  qualified: { bg: 'var(--bullish-muted)', text: 'var(--bullish)', label: 'Qualified' },
};

// Tone map for stat-card icon chips — reuses the site's -muted token pairs instead of ad-hoc hex.
const TONES: Record<string, { bg: string; text: string }> = {
  accent:  { bg: 'var(--accent-muted)',  text: 'var(--accent)'  },
  bullish: { bg: 'var(--bullish-muted)', text: 'var(--bullish)' },
  win:     { bg: 'var(--win-muted)',     text: 'var(--win)'     },
  info:    { bg: 'var(--info-muted)',    text: 'var(--info)'    },
};

export default function ReferralDashboard() {
  const { t } = useLang();
  const { user, loading: authLoading } = useAuth();
  const router = useRouter();
  const [data, setData] = useState<ReferralData | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'history'>('overview');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/referral');
      if (res.ok) {
        const json = await res.json();
        setData(json);
      }
    } catch { /* ignore */ } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authLoading && !user) {
      // Only redirect if there's no localStorage flag indicating prior auth
      const wasAuth = typeof window !== 'undefined' && localStorage.getItem('smt_auth_active') === '1';
      if (!wasAuth) {
        router.push('/sign-up-login-screen');
        return;
      }
    }
    if (user) fetchData();
  }, [user, authLoading, fetchData, router]);

  const handleCopy = async () => {
    if (!data?.referralLink) return;
    try {
      await navigator.clipboard.writeText(data.referralLink);
      track('referral_link_copy', { link: 'signup' });
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  };

  const formatDate = (d: string) =>
    parseServerDate(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

  const formatCurrency = (n: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-base)' }}>
        <Loader2 size={28} className="animate-spin" style={{ color: 'var(--accent)' }} />
      </div>
    );
  }

  if (!data) return null;

  const { profile, referralLink, shortLink, commissionPercent, maxBillingCycles, milestone, foundingMember, stats, attributions, rewards } = data;
  const spotsLeft = Math.max(0, foundingMember.cap - foundingMember.awarded);
  const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || 'https://showmetrade.com';
  const channelReferralLink = `${siteUrl}/channels?ref=${profile.referralCode}`;

  return (
    <div className="min-h-screen pb-24" style={{ background: 'var(--bg-base)' }}>
      <Navbar />
      {/* Header */}
      <div className="sticky top-16 z-10 border-b" style={{ background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(12px)', borderColor: 'var(--border)' }}>
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-3">
          <Link href="/home-feed" className="p-2 rounded-lg transition-colors" style={{ color: 'var(--text-secondary)' }}
            onMouseEnter={e => (e.currentTarget.style.color = 'var(--text-primary)')}
            onMouseLeave={e => (e.currentTarget.style.color = 'var(--text-secondary)')}>
            <ArrowLeft size={18} />
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'var(--accent)' }}>
              <Gift size={16} style={{ color: 'var(--bg-base)' }} />
            </div>
            <div>
              <h1 className="text-base font-bold font-heading" style={{ color: 'var(--text-primary)' }}>{t('Invite & Earn')}</h1>
              <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{t('Earn rewards for every referral')}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">

        {/* Founding Member badge — scarce, permanent, first 100 active traders only */}
        {foundingMember.awarded < foundingMember.cap || foundingMember.number ? (
          <div
            className="rounded-2xl p-5 flex items-center gap-4"
            style={{ background: 'var(--win-muted)', border: '1px solid rgba(176,124,31,0.35)' }}
          >
            <div
              className="w-11 h-11 rounded-full flex items-center justify-center shrink-0"
              style={{ background: 'var(--win)', boxShadow: '0 2px 10px rgba(176,124,31,0.4)' }}
            >
              <Award size={20} style={{ color: '#fff' }} />
            </div>
            <div className="flex-1 min-w-0">
              {foundingMember.number ? (
                <>
                  <p className="text-sm font-bold" style={{ color: 'var(--win)' }}>
                    {t('You are Founding Member')} #{foundingMember.number}
                  </p>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--text-secondary)' }}>
                    {t('A permanent badge for the first 100 traders who actually posted here — you made the cut.')}
                  </p>
                </>
              ) : (
                <>
                  <p className="text-sm font-bold" style={{ color: 'var(--win)' }}>
                    {t('Founding Member badge')} — {spotsLeft} {t('spots left')}
                  </p>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--text-secondary)' }}>
                    {t('Post your first trade idea or forum comment to claim a permanent Founding Member badge — only the first 100 get one.')}
                  </p>
                </>
              )}
              <div className="w-full h-1.5 rounded-full overflow-hidden mt-2" style={{ background: 'var(--bg-sunken)' }}>
                <div
                  className="h-full rounded-full transition-all"
                  style={{ width: `${Math.min(100, (foundingMember.awarded / foundingMember.cap) * 100)}%`, background: 'var(--win)' }}
                />
              </div>
            </div>
          </div>
        ) : null}

        {/* Referral Code Card */}
        <div className="rounded-2xl p-6" style={{ background: 'var(--accent-muted)', border: '1px solid var(--border)' }}>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <p className="text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>{t('YOUR REFERRAL CODE')}</p>
              <div
                className="inline-flex items-center gap-3 px-4 py-2 rounded-xl"
                style={{ background: 'var(--bg-sunken)', border: '1px solid var(--border-input)' }}
              >
                <span className="text-2xl font-bold tracking-widest font-mono-nums" style={{ color: 'var(--text-primary)' }}>
                  {profile.referralCode}
                </span>
              </div>
              <p className="text-xs mt-2" style={{ color: 'var(--text-secondary)' }}>{t('Share this code or your referral link to earn rewards')}</p>
            </div>
            <div className="flex flex-col gap-2">
              <button
                onClick={handleCopy}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all"
                style={{
                  background: copied ? 'var(--bullish-muted)' : 'var(--accent)',
                  color: copied ? 'var(--bullish)' : 'var(--bg-base)',
                  border: `1px solid ${copied ? 'var(--bullish)' : 'var(--accent)'}`,
                }}
              >
                {copied ? <CheckCircle size={14} /> : <Copy size={14} />}
                {copied ? t('Copied!') : t('Copy Link')}
              </button>
            </div>
          </div>

          {/* Shareable links */}
          <div className="mt-4 space-y-2">
            <div className="rounded-xl px-3 py-2.5 flex items-center gap-2" style={{ background: 'var(--bg-sunken)', border: '1px solid var(--border-input)' }}>
              <Gift size={13} style={{ color: 'var(--accent)', flexShrink: 0 }} />
              <span className="text-xs truncate flex-1 font-mono-nums" style={{ color: 'var(--text-primary)' }}>{shortLink}</span>
              <span className="text-[10px] px-1.5 py-0.5 rounded shrink-0 font-semibold" style={{ background: 'var(--accent-muted)', color: 'var(--accent)' }}>{t('SHORT')}</span>
              <button
                onClick={async () => {
                  await navigator.clipboard.writeText(shortLink);
                  track('referral_link_copy', { link: 'short' });
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }}
                className="shrink-0 p-1 rounded" style={{ color: 'var(--accent)' }}>
                <Copy size={12} />
              </button>
            </div>
            <div className="rounded-xl px-3 py-2.5 flex items-center gap-2" style={{ background: 'var(--bg-sunken)', border: '1px solid var(--border-input)' }}>
              <UserPlus size={13} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
              <span className="text-xs truncate flex-1 font-mono-nums" style={{ color: 'var(--text-secondary)' }}>{referralLink}</span>
              <button onClick={handleCopy} className="shrink-0 p-1 rounded" style={{ color: 'var(--text-muted)' }}>
                <Copy size={12} />
              </button>
            </div>
            <div className="rounded-xl px-3 py-2.5 flex items-center gap-2" style={{ background: 'var(--bg-sunken)', border: '1px solid var(--border-input)' }}>
              <Radio size={13} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
              <span className="text-xs truncate flex-1 font-mono-nums" style={{ color: 'var(--text-secondary)' }}>{channelReferralLink}</span>
              <button
                onClick={async () => {
                  await navigator.clipboard.writeText(channelReferralLink);
                  track('referral_link_copy', { link: 'channel' });
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }}
                className="shrink-0 p-1 rounded" style={{ color: 'var(--text-muted)' }}>
                <Copy size={12} />
              </button>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {[
            { label: 'Total Referred', value: stats.totalReferred, icon: Users, tone: 'accent' },
            { label: 'Free Signups', value: stats.freeSignups, icon: UserPlus, tone: 'info' },
            { label: 'Subscribers', value: stats.convertedSubscribers, icon: TrendingUp, tone: 'bullish' },
            { label: 'XP Earned', value: stats.xpEarned.toLocaleString(), icon: Zap, tone: 'win' },
            { label: 'Pending Rewards', value: formatCurrency(stats.pendingRewards), icon: Clock, tone: 'win' },
            { label: 'Approved Rewards', value: formatCurrency(stats.approvedRewards), icon: CheckCircle, tone: 'bullish' },
            { label: 'Lifetime Earnings', value: formatCurrency(stats.lifetimeEarnings), icon: DollarSign, tone: 'accent' },
            { label: 'Creators', value: stats.referredCreators, icon: Radio, tone: 'info' },
          ].map(({ label, value, icon: StatIcon, tone }) => (
            <div key={label} className="card-base p-4">
              <div className="flex items-center gap-2 mb-2">
                <StatIcon size={14} style={{ color: TONES[tone].text }} />
                <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>{t(label)}</span>
              </div>
              <p className="text-xl font-bold font-mono-nums" style={{ color: 'var(--text-primary)' }}>{value}</p>
            </div>
          ))}
        </div>

        {/* Milestone progress */}
        <div className="card-base p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Award size={15} style={{ color: 'var(--win)' }} />
              <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{t('Recruiter Milestones')}</h3>
            </div>
            <span className="text-xs font-mono-nums" style={{ color: 'var(--text-secondary)' }}>
              {milestone.next
                ? `${milestone.totalQualified} / ${milestone.next.count}`
                : `${milestone.totalQualified} ${t('qualified')}`}
            </span>
          </div>
          {milestone.next ? (
            <>
              <div className="w-full h-2 rounded-full overflow-hidden" style={{ background: 'var(--bg-sunken)' }}>
                <div
                  className="h-full rounded-full transition-all"
                  style={{
                    width: `${Math.min(100, (milestone.totalQualified / milestone.next.count) * 100)}%`,
                    background: 'var(--accent)',
                  }}
                />
              </div>
              <p className="text-xs mt-2" style={{ color: 'var(--text-secondary)' }}>
                {t('Qualified referrals to unlock')}{' '}
                <span style={{ color: 'var(--accent)', fontWeight: 600 }}>{milestone.next.label}</span>: {milestone.next.count - milestone.totalQualified}
              </p>
            </>
          ) : (
            <p className="text-xs font-semibold" style={{ color: 'var(--win)' }}>
              🏆 {t("All milestones unlocked — you're a Legend Recruiter!")}
            </p>
          )}
        </div>

        {/* How it works */}
        <div className="card-base p-5">
          <h3 className="text-sm font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>{t('How It Works')}</h3>
          <div className="space-y-3">
            {[
              { step: '1', title: t('Share your link'), desc: t('Share your short /r link with traders and friends'), tone: 'accent' },
              { step: '2', title: t('They sign up & post'), desc: t('When they join and post their first idea, you earn +150 XP and badge progress'), tone: 'accent' },
              { step: '3', title: `${t('They subscribe')} → ${commissionPercent}%`, desc: `${t('Earn')} ${commissionPercent}% ${t('of every payment they make to a paid channel, for up to')} ${maxBillingCycles} ${t('months')}`, tone: 'bullish' },
              { step: '4', title: t('Track & get paid'), desc: t('Cash rewards clear after a 30-day holdback, then are paid out'), tone: 'win' },
            ].map(({ step, title, desc, tone }) => (
              <div key={step} className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 mt-0.5"
                  style={{ background: TONES[tone].bg, color: TONES[tone].text }}>
                  {step}
                </div>
                <div>
                  <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{title}</p>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--text-secondary)' }}>{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Reward Info */}
        <div className="card-base p-5">
          <div className="flex items-center gap-2 mb-3">
            <DollarSign size={15} style={{ color: 'var(--win)' }} />
            <h3 className="text-sm font-semibold" style={{ color: 'var(--win)' }}>{t('Reward Structure')}</h3>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-xl p-3" style={{ background: 'var(--bg-sunken)' }}>
              <div className="flex items-center gap-1 mb-1">
                <Repeat size={11} style={{ color: 'var(--bullish)' }} />
                <p className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>{t('Subscriber')}</p>
              </div>
              <p className="text-lg font-bold font-mono-nums" style={{ color: 'var(--text-primary)' }}>{commissionPercent}%</p>
              <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>{t('Of every payment, up to')} {maxBillingCycles} {t('months')}</p>
            </div>
            <div className="rounded-xl p-3" style={{ background: 'var(--bg-sunken)' }}>
              <div className="flex items-center gap-1 mb-1">
                <Radio size={11} style={{ color: 'var(--info)' }} />
                <p className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>{t('Creator')}</p>
              </div>
              <p className="text-lg font-bold font-mono-nums" style={{ color: 'var(--text-primary)' }}>{commissionPercent}%</p>
              <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>{t('Of their first month launched')}</p>
            </div>
            <div className="rounded-xl p-3" style={{ background: 'var(--bg-sunken)' }}>
              <div className="flex items-center gap-1 mb-1">
                <Zap size={11} style={{ color: 'var(--win)' }} />
                <p className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>{t('Free Signup')}</p>
              </div>
              <p className="text-lg font-bold font-mono-nums" style={{ color: 'var(--text-primary)' }}>+150 XP</p>
              <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>{t('When they post their first idea')}</p>
            </div>
          </div>
          <div className="mt-3 flex items-start gap-2 p-3 rounded-xl" style={{ background: 'var(--win-muted)', border: '1px solid rgba(176,124,31,0.25)' }}>
            <AlertCircle size={13} style={{ color: 'var(--win)', flexShrink: 0, marginTop: 1 }} />
            <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
              {t('Cash rewards have a 30-day holdback (refund window) before approval, then are paid out. Rewards are canceled if the subscription fails or is refunded.')}
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div>
          <div className="flex gap-1 p-1 rounded-xl mb-4" style={{ background: 'var(--bg-sunken)', border: '1px solid var(--border)' }}>
            {(['overview', 'history'] as const).map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className="flex-1 py-2 rounded-lg text-sm font-medium transition-all capitalize"
                style={{
                  background: activeTab === tab ? 'var(--bg-card)' : 'transparent',
                  color: activeTab === tab ? 'var(--text-primary)' : 'var(--text-secondary)',
                  border: activeTab === tab ? '1px solid var(--border)' : '1px solid transparent',
                }}
              >
                {tab === 'overview' ? t('Attributions') : t('Rewards')}
              </button>
            ))}
          </div>

          {activeTab === 'overview' && (
            <div className="card-base overflow-hidden">
              {attributions.length === 0 ? (
                <div className="py-12 text-center">
                  <Users size={32} className="mx-auto mb-3" style={{ color: 'var(--text-dim)' }} />
                  <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>{t('No referrals yet')}</p>
                  <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{t('Share your link to start earning')}</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--border)' }}>
                        {['Type', 'Status', 'Channel', 'Date'].map(h => (
                          <th key={h} className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>{t(h)}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {attributions.map((a, i) => {
                        const sc = STATUS_COLORS[a.status] || STATUS_COLORS.pending;
                        return (
                          <tr key={a.id} style={{ borderBottom: i < attributions.length - 1 ? '1px solid var(--border-subtle)' : 'none' }}>
                            <td className="px-4 py-3">
                              <span className="flex items-center gap-1.5">
                                {a.attributionType === 'creator' ? <Radio size={12} style={{ color: 'var(--info)' }} /> : <UserPlus size={12} style={{ color: 'var(--accent)' }} />}
                                <span className="capitalize text-xs" style={{ color: 'var(--text-primary)' }}>{t(a.attributionType)}</span>
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ background: sc.bg, color: sc.text }}>
                                {t(sc.label)}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              {a.channelId ? (
                                <Link href={`/channels/${a.channelId}`} className="text-xs flex items-center gap-1 transition-colors" style={{ color: 'var(--accent)' }}>
                                  {t('View')} <ExternalLink size={10} />
                                </Link>
                              ) : (
                                <span className="text-xs" style={{ color: 'var(--text-dim)' }}>—</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-xs font-mono-nums" style={{ color: 'var(--text-secondary)' }}>{formatDate(a.createdAt)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'history' && (
            <div className="card-base overflow-hidden">
              {rewards.length === 0 ? (
                <div className="py-12 text-center">
                  <DollarSign size={32} className="mx-auto mb-3" style={{ color: 'var(--text-dim)' }} />
                  <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>{t('No rewards yet')}</p>
                  <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{t('Rewards appear here when referrals convert')}</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--border)' }}>
                        {['Type', 'Amount', 'Status', 'Date'].map(h => (
                          <th key={h} className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>{t(h)}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rewards.map((r, i) => {
                        const sc = STATUS_COLORS[r.rewardStatus] || STATUS_COLORS.pending;
                        const isXp = r.rewardType === 'free_signup_xp';
                        const cycle = r.billingCycleNumber ?? 1;
                        const holdbackActive = r.rewardStatus === 'pending' && r.holdbackUntil && parseServerDate(r.holdbackUntil) > new Date();
                        return (
                          <tr key={r.id} style={{ borderBottom: i < rewards.length - 1 ? '1px solid var(--border-subtle)' : 'none' }}>
                            <td className="px-4 py-3">
                              <span className="text-xs capitalize flex items-center gap-1.5" style={{ color: 'var(--text-primary)' }}>
                                {t(r.rewardType.replaceAll('_', ' '))}
                                {r.rewardType === 'subscriber_referral' && cycle > 1 && (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded font-mono-nums" style={{ background: 'var(--accent-muted)', color: 'var(--accent)' }}>
                                    {t('mo')} {cycle}
                                  </span>
                                )}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <span className="text-sm font-semibold font-mono-nums" style={{ color: isXp ? 'var(--win)' : 'var(--bullish)' }}>
                                {isXp ? `+${Number(r.rewardAmount)} XP` : formatCurrency(Number(r.rewardAmount))}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ background: sc.bg, color: sc.text }}>
                                {t(sc.label)}
                              </span>
                              {holdbackActive && (
                                <span className="block text-[10px] mt-1" style={{ color: 'var(--text-muted)' }}>
                                  {t('Clears')} {formatDate(r.holdbackUntil!)}
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-xs font-mono-nums" style={{ color: 'var(--text-secondary)' }}>{formatDate(r.createdAt)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
